import React, { useEffect, useState, useCallback } from 'react';
import {
  X, UserPlus, Key, Trash2, Eye, EyeOff, Shield, AlertCircle, Check,
  Edit3, Power, PowerOff, Save, XCircle, Mail, User,
} from 'lucide-react';

import {
  fetchAdminUsers, createAdminUser, changeAdminPassword, deleteAdminUser,
  updateAdminUser, toggleAdminUserActive,
} from './api';
import type { AdminListUser } from './api';

interface UserManagementProps {
  currentAdminId?: number;
  onClose: () => void;
}

type ModalView = 'list' | 'create' | 'edit' | 'password' | 'delete';

export default function UserManagement({ currentAdminId, onClose }: UserManagementProps) {
  const [users, setUsers] = useState<AdminListUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalView, setModalView] = useState<ModalView>('list');
  const [selectedUser, setSelectedUser] = useState<AdminListUser | null>(null);
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formRole, setFormRole] = useState('user');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const showToast = useCallback((type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    const data = await fetchAdminUsers();
    setUsers(data);
    setLoading(false);
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const resetForm = () => {
    setFormName(''); setFormEmail(''); setFormPassword('');
    setFormRole('user'); setShowPassword(false); setSaving(false);
  };

  const openCreate = () => { resetForm(); setSelectedUser(null); setModalView('create'); };
  const openEdit = (user: AdminListUser) => {
    resetForm(); setSelectedUser(user);
    setFormName(user.name); setFormEmail(user.email); setFormRole(user.role);
    setModalView('edit');
  };
  const openPassword = (user: AdminListUser) => { resetForm(); setSelectedUser(user); setModalView('password'); };
  const openDelete = (user: AdminListUser) => { setSelectedUser(user); setModalView('delete'); };
  const goBack = () => { resetForm(); setSelectedUser(null); setModalView('list'); };

  const handleCreate = async () => {
    if (!formName.trim() || !formEmail.trim() || formPassword.length < 8) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formEmail.trim())) { showToast('error', 'Please enter a valid email address.'); return; }
    setSaving(true);
    const result = await createAdminUser({ name: formName.trim(), email: formEmail.trim(), password: formPassword, role: formRole });
    setSaving(false);
    if (result.success) { showToast('success', 'User "' + formEmail.trim() + '" created successfully.'); goBack(); loadUsers(); }
    else { showToast('error', result.error || 'Failed to create user.'); }
  };

  const handleUpdate = async () => {
    if (!selectedUser || !formName.trim() || !formEmail.trim()) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formEmail.trim())) { showToast('error', 'Please enter a valid email address.'); return; }
    setSaving(true);
    const result = await updateAdminUser(selectedUser.id, { name: formName.trim(), email: formEmail.trim(), role: formRole });
    setSaving(false);
    if (result.success) { showToast('success', 'User "' + formName.trim() + '" updated successfully.'); goBack(); loadUsers(); }
    else { showToast('error', result.error || 'Failed to update user.'); }
  };

  const handleChangePassword = async () => {
    if (!selectedUser || formPassword.length < 8) return;
    setSaving(true);
    const result = await changeAdminPassword(selectedUser.id, formPassword);
    setSaving(false);
    if (result.success) { showToast('success', 'Password changed for "' + selectedUser.email + '".'); goBack(); }
    else { showToast('error', result.error || 'Failed to change password.'); }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    setSaving(true);
    const result = await deleteAdminUser(selectedUser.id);
    setSaving(false);
    if (result.success) { showToast('success', 'User "' + selectedUser.email + '" deleted.'); goBack(); loadUsers(); }
    else { showToast('error', result.error || 'Failed to delete user.'); }
  };

  const handleToggleActive = async (user: AdminListUser) => {
    const newActive = !user.is_active;
    const result = await toggleAdminUserActive(user.id, newActive);
    if (result.success) { showToast('success', 'User "' + user.name + '" ' + (newActive ? 'activated' : 'deactivated') + '.'); loadUsers(); }
    else { showToast('error', result.error || 'Failed to update user status.'); }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (modalView === 'list') onClose(); else goBack();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={handleBackdropClick}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            {modalView !== 'list' && (
              <button onClick={goBack} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 mr-1">
                <XCircle className="w-4 h-4" />
              </button>
            )}
            <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Shield className="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">
                {modalView === 'list' && 'User Management'}
                {modalView === 'create' && 'Add New User'}
                {modalView === 'edit' && 'Edit User'}
                {modalView === 'password' && 'Change Password'}
                {modalView === 'delete' && 'Delete User'}
              </h2>
              <p className="text-xs text-slate-500">
                {modalView === 'list' && 'Manage admin users'}
                {modalView === 'create' && 'Create a new admin account'}
                {modalView === 'edit' && selectedUser?.email}
                {modalView === 'password' && selectedUser?.email}
                {modalView === 'delete' && selectedUser?.email}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toast */}
        {toast && (
          <div className={'mx-6 mt-4 px-4 py-3 rounded-xl text-sm font-medium flex items-center gap-2 ' + (toast.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200')}>
            {toast.type === 'success' ? <Check className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
            {toast.message}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* LIST VIEW */}
          {modalView === 'list' && (
            <div className="space-y-4">
              <button onClick={openCreate} className="w-full h-11 rounded-xl border-2 border-dashed border-indigo-200 text-sm font-semibold text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300 flex items-center justify-center gap-2 transition-all">
                <UserPlus className="w-4 h-4" /> Add New User
              </button>
              {loading ? (
                <div className="text-center py-8 text-sm text-slate-400">Loading users...</div>
              ) : users.length === 0 ? (
                <div className="text-center py-8 text-sm text-slate-400">No admin users found.</div>
              ) : (
                <div className="space-y-2">
                  {users.map((user) => {
                    const isCurrentUser = user.id === currentAdminId;
                    const isActive = user.is_active !== 0;
                    return (
                      <div key={user.id} className={'rounded-xl border p-4 flex items-center justify-between transition-all ' + (isActive ? 'bg-white border-slate-200 hover:shadow-sm' : 'bg-slate-50 border-slate-200/60 opacity-60')}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ' + (isActive ? 'bg-gradient-to-br from-indigo-100 to-indigo-50' : 'bg-slate-200')}>
                            <span className={'text-sm font-bold ' + (isActive ? 'text-indigo-600' : 'text-slate-400')}>
                              {(user.name || user.email)[0].toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-slate-900 truncate">{user.name}</span>
                              <span className={'text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md ' + (user.role === 'admin' ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-500')}>{user.role}</span>
                              {isCurrentUser && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-green-50 text-green-600">You</span>}
                              {!isActive && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-red-50 text-red-500">Inactive</span>}
                            </div>
                            <p className="text-xs text-slate-400 truncate">{user.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0 ml-3">
                          <button onClick={() => openEdit(user)} className="p-2 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all" title="Edit User"><Edit3 className="w-4 h-4" /></button>
                          <button onClick={() => openPassword(user)} className="p-2 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-all" title="Change Password"><Key className="w-4 h-4" /></button>
                          {!isCurrentUser && (
                            <button onClick={() => handleToggleActive(user)} className={'p-2 rounded-lg transition-all ' + (isActive ? 'text-slate-400 hover:text-orange-600 hover:bg-orange-50' : 'text-green-400 hover:text-green-600 hover:bg-green-50')} title={isActive ? 'Deactivate User' : 'Activate User'}>
                              {isActive ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                            </button>
                          )}
                          {!isCurrentUser && (
                            <button onClick={() => openDelete(user)} className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-all" title="Delete User"><Trash2 className="w-4 h-4" /></button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* CREATE VIEW */}
          {modalView === 'create' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5"><User className="w-3 h-3 inline mr-1" />Name</label>
                  <input type="text" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Full Name" autoComplete="off" data-form-type="other" className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5"><Mail className="w-3 h-3 inline mr-1" />Email</label>
                  <input type="email" value={formEmail} onChange={(e) => setFormEmail(e.target.value)} placeholder="user@example.com" autoComplete="off" data-form-type="other" className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all" />
                  {formEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formEmail) && <p className="text-[11px] text-red-500 mt-1">Invalid email format</p>}
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5"><Key className="w-3 h-3 inline mr-1" />Password</label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} value={formPassword} onChange={(e) => setFormPassword(e.target.value)} placeholder="Min. 8 characters" autoComplete="new-password" data-form-type="other" className="w-full h-10 px-3 pr-10 rounded-xl border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {formPassword.length > 0 && formPassword.length < 8 && <p className="text-[11px] text-red-500 mt-1">Password must be at least 8 characters</p>}
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2"><Shield className="w-3 h-3 inline mr-1" />Role</label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="create-role"
                        checked={formRole === 'user'}
                        onChange={() => setFormRole('user')}
                        className="w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-500"
                      />
                      <span className="text-sm font-medium text-slate-700">User</span>
                      <span className="text-[10px] text-slate-400">— View &amp; manage leads</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="create-role"
                        checked={formRole === 'admin'}
                        onChange={() => setFormRole('admin')}
                        className="w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-500"
                      />
                      <span className="text-sm font-medium text-slate-700">Admin</span>
                      <span className="text-[10px] text-slate-400">— Full access + delete</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                <button onClick={handleCreate} disabled={saving || !formName.trim() || !formEmail.trim() || formPassword.length < 8 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formEmail)} className="h-10 px-5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2">
                  {saving ? 'Saving...' : <><UserPlus className="w-4 h-4" /> Add User</>}
                </button>
                <button onClick={goBack} className="h-10 px-4 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
              </div>
            </div>
          )}

          {/* EDIT VIEW */}
          {modalView === 'edit' && selectedUser && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5"><User className="w-3 h-3 inline mr-1" />Name</label>
                  <input type="text" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Full Name" autoComplete="off" data-form-type="other" className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5"><Mail className="w-3 h-3 inline mr-1" />Email</label>
                  <input type="email" value={formEmail} onChange={(e) => setFormEmail(e.target.value)} placeholder="user@example.com" autoComplete="off" data-form-type="other" className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all" />
                  {formEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formEmail) && <p className="text-[11px] text-red-500 mt-1">Invalid email format</p>}
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5"><Shield className="w-3 h-3 inline mr-1" />Role</label>
                  <select value={formRole} onChange={(e) => setFormRole(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm text-slate-700 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 bg-white transition-all">
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                <button onClick={handleUpdate} disabled={saving || !formName.trim() || !formEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formEmail)} className="h-10 px-5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2">
                  {saving ? 'Saving...' : <><Save className="w-4 h-4" /> Save Changes</>}
                </button>
                <button onClick={goBack} className="h-10 px-4 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
              </div>
            </div>
          )}

          {/* PASSWORD VIEW */}
          {modalView === 'password' && selectedUser && (
            <div className="space-y-4">
              <div className="bg-amber-50/50 rounded-xl p-4 border border-amber-100">
                <p className="text-sm text-amber-800">
                  Setting a new password for <strong>{selectedUser.name}</strong> ({selectedUser.email}).
                  {selectedUser.id === currentAdminId && (
                    <span className="block mt-1 text-xs font-medium text-amber-600">This is your own account — you will need to use the new password next time you log in.</span>
                  )}
                </p>
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5"><Key className="w-3 h-3 inline mr-1" />New Password</label>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} value={formPassword} onChange={(e) => setFormPassword(e.target.value)} placeholder="Min. 8 characters" autoComplete="new-password" data-form-type="other" autoFocus className="w-full h-10 px-3 pr-10 rounded-xl border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {formPassword.length > 0 && formPassword.length < 8 && <p className="text-[11px] text-red-500 mt-1">Password must be at least 8 characters</p>}
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                <button onClick={handleChangePassword} disabled={saving || formPassword.length < 8} className="h-10 px-5 rounded-xl bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 focus:ring-4 focus:ring-amber-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2">
                  {saving ? 'Saving...' : <><Key className="w-4 h-4" /> Save Password</>}
                </button>
                <button onClick={goBack} className="h-10 px-4 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
              </div>
            </div>
          )}

          {/* DELETE VIEW */}
          {modalView === 'delete' && selectedUser && (
            <div className="space-y-4">
              <div className="text-center py-4">
                <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="w-7 h-7 text-red-500" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">Delete this user?</h3>
                <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto">
                  This will permanently remove <strong>{selectedUser.name}</strong> ({selectedUser.email}). They will no longer be able to log in.
                </p>
              </div>
              <div className="flex items-center justify-center gap-3 pt-2 border-t border-slate-100">
                <button onClick={goBack} className="h-10 px-5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
                <button onClick={handleDelete} disabled={saving} className="h-10 px-5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 focus:ring-4 focus:ring-red-500/30 disabled:opacity-40 transition-all flex items-center gap-2">
                  {saving ? 'Deleting...' : <><Trash2 className="w-4 h-4" /> Delete User</>}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
